import { SkeletonText, Tooltip, Icon, colors } from '@dev-spendesk/grapes';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';

import { QueryError } from 'src/core/common/components/QueryError';
import { QuerySuspense } from 'src/core/common/components/QuerySuspense';
import { useParams } from 'src/core/common/hooks/useParams';
import { useTranslation } from 'src/core/common/hooks/useTranslation';
import { routes, routeFor } from 'src/core/constants/routes';
import { useCompanyId } from 'src/core/modules/app/hooks/useCompanyId';
import {
  getPercentageClassName,
  Percentage,
} from 'src/core/modules/budgets/components/Percentage';

import { useBudgetsOverviewList } from './useBudgetsOverviewList';
import { type PeriodRangePayload } from '../../../../models/periodRange';

export const BudgetsOverviewList = ({
  budgetId,
  periodRange,
}: {
  budgetId: string;
  periodRange: PeriodRangePayload;
}) => {
  const history = useHistory();
  const { t } = useTranslation('global');
  const budgetsOverviewListQueryState = useBudgetsOverviewList({
    budgetId,
    periodRange,
  });
  const companyId = useCompanyId();
  const { id: currentBudgetId } = useParams(routes.BUDGET_OVERVIEW.path);
  const { subBudgetId: currentSubBudgetId } = useParams(
    routes.SUB_BUDGET_OVERVIEW.path,
  );

  const handleSelectBudget = (id: string) => {
    const route = routeFor(routes.BUDGET_OVERVIEW.path, {
      company: companyId,
      id,
    });
    history.push(route);
  };

  return (
    <div className="box mb-24 max-h-max w-[260px] overflow-y-auto p-0 pb-8">
      <div className="text-content-primary title-xl px-24 py-16">
        {t('budget.overviewPage.yourBudgets')}
      </div>
      <div className="separator mb-8" />
      <QuerySuspense
        queryState={budgetsOverviewListQueryState}
        loading={<Loading />}
        fallback={(error) => (
          <QueryError queryError={error} componentType="Callout" />
        )}
      >
        {(data) => (
          <div>
            {data.map((budget) => (
              <>
                <ListItem
                  key={budget.id}
                  name={budget.name}
                  usedRatio={budget.usedRatio}
                  isSelected={
                    budget.id === currentBudgetId && !currentSubBudgetId
                  }
                  isArchived={budget.isArchived}
                  onClick={() => handleSelectBudget(budget.id)}
                />
                {budget.subBudgets.length > 0 && (
                  <SubList items={budget.subBudgets} budgetId={budgetId} />
                )}
              </>
            ))}
          </div>
        )}
      </QuerySuspense>
    </div>
  );
};

const Loading = () => {
  return (
    <div className="flex flex-col gap-8">
      <div className="flex items-center gap-8 px-8">
        <div className="flex w-32 items-center justify-center">
          <SkeletonText className="h-16 w-16" />
        </div>
        <div className="grow">
          <SkeletonText className="mb-4 h-16 w-full" />
          <SkeletonText className="h-16 w-[88%]" />
        </div>
      </div>
      <div className="px-8 pl-48">
        <SkeletonText className="mb-4 h-16 w-full" />
        <SkeletonText className="h-16 w-[50%]" />
      </div>
      <div className="px-8 pl-48">
        <SkeletonText className="mb-4 h-16 w-full" />
        <SkeletonText className="h-16 w-[76%]" />
      </div>
      <div className="px-8 pl-48">
        <SkeletonText className="mb-4 h-16 w-full" />
        <SkeletonText className="h-16 w-[80%]" />
      </div>
      <div className="px-8 pl-48">
        <SkeletonText className="mb-4 h-16 w-full" />
        <SkeletonText className="h-16 w-[83%]" />
      </div>
      <div className="px-8 pl-48">
        <SkeletonText className="mb-4 h-16 w-full" />
        <SkeletonText className="h-16 w-[90%]" />
      </div>
    </div>
  );
};

const SubList = ({
  items,
  budgetId,
}: {
  budgetId: string;
  items: {
    id: string;
    name: string;
    usedRatio: number;
    isSelected: boolean;
    isArchived?: boolean;
  }[];
}) => {
  const history = useHistory();
  const companyId = useCompanyId();
  const handleSelect = (id: string) => {
    const route = routeFor(routes.SUB_BUDGET_OVERVIEW.path, {
      company: companyId,
      id: budgetId,
      subBudgetId: id,
    });
    history.push(route);
  };
  const { subBudgetId: currentSubBudgetId } = useParams(
    routes.SUB_BUDGET_OVERVIEW.path,
  );

  return (
    <>
      {items.map((item) => (
        <ListItem
          key={item.id}
          name={item.name}
          usedRatio={item.usedRatio}
          isSelected={item.id === currentSubBudgetId}
          isArchived={item.isArchived}
          isSubItem
          onClick={() => handleSelect(item.id)}
        />
      ))}
    </>
  );
};

const ListItem = ({
  name,
  usedRatio,
  isSelected,
  isSubItem,
  isArchived,
  onClick,
}: {
  name: string;
  usedRatio: number;
  isSelected: boolean;
  isSubItem?: boolean;
  isArchived?: boolean;
  onClick: () => void;
}) => {
  const { t } = useTranslation('global');

  return (
    <div
      role="button"
      aria-label={name}
      tabIndex={0}
      className={classNames(
        'hover:bg-background-primary-hover relative flex cursor-pointer items-center px-8 py-8',
        isSelected &&
          'bg-background-primary-selected hover:bg-background-primary-selected hover:cursor-default',
        isSubItem && 'body-m pl-40',
        !isSubItem && 'pl-24',
      )}
      onClick={onClick}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          onClick();
        }
      }}
    >
      {isSelected && (
        <div className="bg-background-primary-brand-default absolute top-0 left-0 h-full w-[2px]" />
      )}
      <div className="flex min-w-0 flex-1 flex-col">
        <div
          className={classNames(
            'text-content-primary flex items-center gap-4',
            isSubItem && 'body-m',
            !isSubItem && 'title-m',
          )}
        >
          <span className="truncate">{name}</span>
          {isArchived && (
            <Tooltip
              content={t('budget.overviewPage.expenseCategoryDeleted')}
              placement="top"
            >
              <Icon
                size="s"
                color={colors.contentDecorativeIcon}
                name="circle-information"
              />
            </Tooltip>
          )}
        </div>

        <div
          className={classNames(
            getPercentageClassName({ over: usedRatio > 1 }),
            isSubItem && 'body-m',
            !isSubItem && 'title-m',
          )}
        >
          <Percentage ratio={usedRatio} />
        </div>
      </div>
    </div>
  );
};
